import React, { type ReactNode } from "react"
import { useWindowDimensions } from "react-native"
import { cn } from "../styles/utils"
import { CloseButton } from "./CloseButton"
import { Text } from "./Text"
import {
  Modal as GluestackModal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "./gluestack/modal"

export interface ModalProps {
  isVisible: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "full"
  showCloseButton?: boolean
  footer?: ReactNode
  bodyClassName?: string
  contentClassName?: string
  closeButtonClassName?: string
  footerClassName?: string
  headerClassName?: string
  closeOnBackdropPress?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  isVisible,
  onClose,
  title,
  children,
  size = "md",
  showCloseButton = true,
  footer,
  bodyClassName,
  contentClassName,
  closeButtonClassName,
  footerClassName,
  headerClassName,
  closeOnBackdropPress = true,
}) => {
  const { height: viewportHeight } = useWindowDimensions()

  // Calculate max height for modal content (80% of viewport height)
  const maxModalHeight = Math.floor(viewportHeight * 0.8)

  // Calculate header height (approximate)
  const headerHeight = title ? 72 : 0 // Header with padding and border

  // Calculate footer height (approximate)
  const footerHeight = footer ? 72 : 0 // Footer with padding and border

  // Calculate available height for modal body
  const maxBodyHeight = maxModalHeight - headerHeight - footerHeight

  return (
    <GluestackModal isOpen={isVisible} onClose={onClose} size={size}>
      <ModalBackdrop onPress={closeOnBackdropPress ? onClose : undefined} />
      <ModalContent
        className={cn(
          "bg-background-0 rounded-t-xl relative overflow-visible shadow-xl",
          contentClassName,
        )}
        style={{ maxHeight: maxModalHeight }}
      >
        {showCloseButton && (
          <CloseButton
            onPress={onClose}
            variant="modal"
            className={closeButtonClassName}
          />
        )}

        {title && (
          <ModalHeader
            className={cn(
              "flex-row justify-between items-center border-b border-outline-200",
              headerClassName,
            )}
          >
            <Text className="text-lg font-bold">{title}</Text>
          </ModalHeader>
        )}

        <ModalBody
          className={cn("m-0", bodyClassName)}
          style={{ maxHeight: maxBodyHeight }}
          showsVerticalScrollIndicator={true}
        >
          {children}
        </ModalBody>

        {footer && (
          <ModalFooter
            className={cn("border-t border-gray-200 p-4", footerClassName)}
          >
            {footer}
          </ModalFooter>
        )}
      </ModalContent>
    </GluestackModal>
  )
}
